import styled from 'styled-components';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { Edit16 } from 'ui/icons';
import { body2Bold, spacing } from 'ui/theme';
import { Tooltip, TipPlacement } from 'ui/tooltip';
import {
  getCurrentOrEditedOrder,
  getHasDisplayPriority,
} from 'modules/campaigns/editor/components/utils';
import { useDisplayPriorityContext } from 'modules/placements/context/DisplayPriorityContext';
import { useCampaign } from '../../BaseSendEditor.hooks';
import { BodyPrimaryText } from '../DisplayUntil/DisplayUntil.styles';

const DisplayPriorityWrapper = styled.div`
  margin-bottom: ${spacing.plusHalf};
`;

const HeaderText = styled.p`
  ${body2Bold};
  margin-bottom: 0;
`;

const DisplayPriorityEdit = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.half};
`;

const TOOLTIP_COPY =
  'Must first create campaign or choose a template to select the display priority';

export const EMBEDDED_DISPLAY_PRIORITY_TEST_ID = 'embedded-display-priority';

export interface EmbeddedDisplayPriorityProps {
  /** The campaign ID from node data */
  campaignId?: number;
  /** Is the edit button disabled */
  isDisabled: boolean;
  /** Callback when button is clicked */
  onOpen: () => void;
}

export const EmbeddedDisplayPriority = ({
  campaignId,
  isDisabled,
  onOpen,
}: EmbeddedDisplayPriorityProps) => {
  const { campaign } = useCampaign();
  const { embeddedPriorityOrder } = campaign;
  const { campaignDisplayPriorityOrder } = useDisplayPriorityContext();
  const hasDisplayPriorityOrder = getHasDisplayPriority(embeddedPriorityOrder);
  const currentOrEditedOrder = getCurrentOrEditedOrder(campaignDisplayPriorityOrder, campaignId);

  return (
    <DisplayPriorityWrapper data-test={EMBEDDED_DISPLAY_PRIORITY_TEST_ID}>
      <DisplayPriorityEdit>
        <HeaderText>Display priority</HeaderText>
        {hasDisplayPriorityOrder ? (
          <SecondaryButton size={BUTTON_SIZES.SMALL} onClick={onOpen} isDisabled={isDisabled}>
            <Edit16 /> Edit
          </SecondaryButton>
        ) : (
          <Tooltip
            isEnabled={!campaignId}
            placement={TipPlacement.Left}
            tooltipContent={TOOLTIP_COPY}
          >
            <SecondaryButton size={BUTTON_SIZES.SMALL} onClick={onOpen} isDisabled={!campaignId}>
              Select display priority
            </SecondaryButton>
          </Tooltip>
        )}
      </DisplayPriorityEdit>
      <BodyPrimaryText>
        {hasDisplayPriorityOrder && `${currentOrEditedOrder} campaigns`}
      </BodyPrimaryText>
    </DisplayPriorityWrapper>
  );
};
